theme.beforeAfter = (function() {
  'use strict';
  
  function BeforeAfter() {
    class BeforeAfterComparison {
      constructor(container) {
        this.container = container;
        this.images = container.querySelector('.before-after-images');
        this.afterWrapper = container.querySelector('.after-image-wrapper');
        this.separator = container.querySelector('.border-separator');
        this.handle = container.querySelector('.border-handle');
        
        // Validate required elements
        if (!this.images || !this.afterWrapper || !this.separator || !this.handle) {
          console.warn('Before-after: Missing required elements');
          return;
        }
        
        // State management
        this.isDragging = false;
        this.isInitialized = false;
        this.currentX = 50;
        this.containerRect = null;
        this.animationId = null;
        
        // Performance optimizations
        this.shouldUpdate = true;
        this.lastUpdateTime = 0;
        this.throttleDelay = 16; // ~60fps
        
        // Bind methods
        this.boundHandleStart = this.handleStart.bind(this);
        this.boundHandleMove = this.handleMove.bind(this);
        this.boundHandleEnd = this.handleEnd.bind(this);
        this.boundHandleKeyDown = this.handleKeyDown.bind(this);
        this.boundHandleResize = this.debounce(this.handleResize.bind(this), 250);
        this.boundHandleFocus = this.handleFocus.bind(this);
        this.boundHandleBlur = this.handleBlur.bind(this);
        
        this.init();
      }
      
      init() {
        if (this.isInitialized) return;
        
        try {
          // Initial setup
          this.updateContainerRect();
          this.setupEventListeners();
          this.updatePosition(50);
          this.isInitialized = true;
          
          // Add loaded class for CSS transitions
          this.container.classList.add('before-after-loaded');
          
        } catch (error) {
          console.error('Before-after initialization failed:', error);
        }
      }
      
      setupEventListeners() {
        // Mouse events
        this.separator.addEventListener('mousedown', this.boundHandleStart);
        
        // Touch events with proper passive handling
        this.separator.addEventListener('touchstart', this.boundHandleStart, { passive: false });
        
        // Keyboard navigation
        this.separator.addEventListener('keydown', this.boundHandleKeyDown);
        
        // Focus management
        this.separator.addEventListener('focus', this.boundHandleFocus);
        this.separator.addEventListener('blur', this.boundHandleBlur);
        
        // Window resize handling
        window.addEventListener('resize', this.boundHandleResize);
        
        // Prevent context menu on handle
        this.handle.addEventListener('contextmenu', (e) => e.preventDefault());
      }
      
      handleStart(e) {
        if (this.isDragging) return;
        
        this.isDragging = true;
        this.updateContainerRect();
        
        const clientX = this.getClientX(e);
        this.startX = clientX;
        this.initialPosition = this.currentX;
        
        // Prevent default behaviors
        e.preventDefault();
        e.stopPropagation();
        
        // Add event listeners for move and end
        if (e.type === 'mousedown') {
          document.addEventListener('mousemove', this.boundHandleMove, { passive: false });
          document.addEventListener('mouseup', this.boundHandleEnd);
        } else {
          document.addEventListener('touchmove', this.boundHandleMove, { passive: false });
          document.addEventListener('touchend', this.boundHandleEnd);
          document.addEventListener('touchcancel', this.boundHandleEnd);
        }
        
        // Visual feedback
        this.updateCursor('grabbing');
        this.separator.classList.add('dragging');
        
        // Haptic feedback on mobile
        this.vibrate(50);
        
        // Disable text selection
        document.body.style.userSelect = 'none';
      }
      
      handleMove(e) {
        if (!this.isDragging || !this.shouldUpdate) return;
        
        const currentTime = performance.now();
        if (currentTime - this.lastUpdateTime < this.throttleDelay) return;
        
        this.cancelAnimationFrame();
        this.animationId = requestAnimationFrame(() => {
          const clientX = this.getClientX(e);
          const deltaX = clientX - this.startX;
          const percentDelta = (deltaX / this.containerRect.width) * 100;
          const newPosition = this.clampPosition(this.initialPosition + percentDelta);
          
          this.updatePosition(newPosition);
          this.lastUpdateTime = currentTime;
        });
        
        // Prevent scrolling on touch devices
        e.preventDefault();
      }
      
      handleEnd(e) {
        if (!this.isDragging) return;
        
        this.isDragging = false;
        this.currentX = parseFloat(this.separator.style.left) || 50;
        
        // Remove event listeners
        document.removeEventListener('mousemove', this.boundHandleMove);
        document.removeEventListener('mouseup', this.boundHandleEnd);
        document.removeEventListener('touchmove', this.boundHandleMove);
        document.removeEventListener('touchend', this.boundHandleEnd);
        document.removeEventListener('touchcancel', this.boundHandleEnd);
        
        // Reset visual states
        this.updateCursor('grab');
        this.separator.classList.remove('dragging');
        document.body.style.userSelect = '';
        
        // Clean up animation frame
        this.cancelAnimationFrame();
        
        // Snap to edges if close
        this.snapToEdges();
      }
      
      handleKeyDown(e) {
        const FINE_STEP = 1;
        const COARSE_STEP = 5;
        let newPosition = this.currentX;
        let shouldPrevent = true;
        
        switch(e.key) {
          case 'ArrowLeft':
            newPosition = this.clampPosition(this.currentX - (e.shiftKey ? FINE_STEP : COARSE_STEP));
            break;
          case 'ArrowRight':
            newPosition = this.clampPosition(this.currentX + (e.shiftKey ? FINE_STEP : COARSE_STEP));
            break;
          case 'Home':
            newPosition = 0;
            break;
          case 'End':
            newPosition = 100;
            break;
          case 'PageUp':
            newPosition = this.clampPosition(this.currentX - 10);
            break;
          case 'PageDown':
            newPosition = this.clampPosition(this.currentX + 10);
            break;
          default:
            shouldPrevent = false;
        }
        
        if (shouldPrevent) {
          e.preventDefault();
          this.smoothUpdatePosition(newPosition);
          this.currentX = newPosition;
          this.vibrate(25);
        }
      }
      
      handleFocus() {
        this.separator.classList.add('focused');
      }
      
      handleBlur() {
        this.separator.classList.remove('focused');
      }
      
      handleResize() {
        this.updateContainerRect();
        this.updatePosition(this.currentX);
      }
      
      // Utility methods
      getClientX(e) {
        return e.type.includes('touch') ? e.touches[0]?.clientX || e.changedTouches[0]?.clientX : e.clientX;
      }
      
      updateContainerRect() {
        if (this.images) {
          this.containerRect = this.images.getBoundingClientRect();
        }
      }
      
      clampPosition(position) {
        return Math.min(100, Math.max(0, position));
      }
      
      updatePosition(percent) {
        if (!this.afterWrapper || !this.separator) return;
        
        const clampedPercent = this.clampPosition(percent);
        
        // Update clip path with GPU acceleration
        this.afterWrapper.style.clipPath = `polygon(${clampedPercent}% 0, 100% 0, 100% 100%, ${clampedPercent}% 100%)`;
        
        // Update separator position
        this.separator.style.left = `${clampedPercent}%`;
        
        // Update ARIA value
        this.separator.setAttribute('aria-valuenow', Math.round(clampedPercent));
        
        // Trigger custom event
        this.dispatchCustomEvent('beforeAfterUpdate', { position: clampedPercent });
      }
      
      smoothUpdatePosition(percent) {
        if (!this.afterWrapper || !this.separator) return;
        
        const startPercent = this.currentX;
        const endPercent = this.clampPosition(percent);
        const duration = 200;
        const startTime = performance.now();
        
        const animate = (currentTime) => {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);
          
          // Ease out cubic
          const easeProgress = 1 - Math.pow(1 - progress, 3);
          const currentPercent = startPercent + (endPercent - startPercent) * easeProgress;
          
          this.updatePosition(currentPercent);
          
          if (progress < 1) {
            requestAnimationFrame(animate);
          }
        };
        
        requestAnimationFrame(animate);
      }
      
      snapToEdges() {
        const snapThreshold = 5;
        let targetPosition = this.currentX;
        
        if (this.currentX <= snapThreshold) {
          targetPosition = 0;
        } else if (this.currentX >= 100 - snapThreshold) {
          targetPosition = 100;
        }
        
        if (targetPosition !== this.currentX) {
          this.smoothUpdatePosition(targetPosition);
          this.currentX = targetPosition;
        }
      }
      
      updateCursor(cursor) {
        this.handle.style.cursor = cursor;
        document.body.style.cursor = cursor === 'grab' ? '' : cursor;
      }
      
      vibrate(duration) {
        if ('vibrate' in navigator) {
          navigator.vibrate(duration);
        }
      }
      
      dispatchCustomEvent(eventName, detail) {
        const event = new CustomEvent(eventName, {
          detail: detail,
          bubbles: true,
          cancelable: true
        });
        this.container.dispatchEvent(event);
      }
      
      cancelAnimationFrame() {
        if (this.animationId) {
          cancelAnimationFrame(this.animationId);
          this.animationId = null;
        }
      }
      
      debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
          const later = () => {
            clearTimeout(timeout);
            func.apply(this, args);
          };
          clearTimeout(timeout);
          timeout = setTimeout(later, wait);
        };
      }
      
      // Public methods
      setPosition(percent) {
        const clampedPercent = this.clampPosition(percent);
        this.smoothUpdatePosition(clampedPercent);
        this.currentX = clampedPercent;
      }
      
      getPosition() {
        return this.currentX;
      }
      
      reset() {
        this.setPosition(50);
      }
      
      // Cleanup
      destroy() {
        if (!this.isInitialized) return;
        
        // Remove event listeners
        this.separator.removeEventListener('mousedown', this.boundHandleStart);
        this.separator.removeEventListener('touchstart', this.boundHandleStart);
        this.separator.removeEventListener('keydown', this.boundHandleKeyDown);
        this.separator.removeEventListener('focus', this.boundHandleFocus);
        this.separator.removeEventListener('blur', this.boundHandleBlur);
        window.removeEventListener('resize', this.boundHandleResize);
        
        // Clean up document listeners
        document.removeEventListener('mousemove', this.boundHandleMove);
        document.removeEventListener('mouseup', this.boundHandleEnd);
        document.removeEventListener('touchmove', this.boundHandleMove);
        document.removeEventListener('touchend', this.boundHandleEnd);
        document.removeEventListener('touchcancel', this.boundHandleEnd);
        
        // Cancel any pending animations
        this.cancelAnimationFrame();
        
        // Reset styles
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        
        this.isInitialized = false;
      }
    }
    
    // Initialize with Intersection Observer for better performance
    const initializeWithIntersectionObserver = () => {
      const containers = document.querySelectorAll('.before-after');
      
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const instance = new BeforeAfterComparison(entry.target);
              // Store instance for potential cleanup
              entry.target._beforeAfterInstance = instance;
              observer.unobserve(entry.target);
            }
          });
        }, {
          rootMargin: '50px',
          threshold: 0.1
        });
        
        containers.forEach(container => {
          observer.observe(container);
        });
      } else {
        // Fallback for older browsers
        containers.forEach(container => {
          const instance = new BeforeAfterComparison(container);
          container._beforeAfterInstance = instance;
        });
      }
    };
    
    // Auto-initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeWithIntersectionObserver);
    } else {
      initializeWithIntersectionObserver();
    }
    
    // Public API
    return {
      init: initializeWithIntersectionObserver,
      instances: () => {
        const containers = document.querySelectorAll('.before-after');
        return Array.from(containers).map(container => container._beforeAfterInstance).filter(Boolean);
      }
    };
  }
  
  return BeforeAfter;
})();